<!--
@Author: Kleber Pinel <kleberdasilva>
@Date:   2019-02-05T09:54:43-08:00
@Email:  kleber@hackcapital.com
@Last modified by:   kleberdasilva
@Last modified time: 2019-02-28T17:23:51-08:00
-->

<template>
  <LdAppLayoutPresenter
    :smsUsage="smsUsageInfo"
    :activeApp="activeApp"
    :addonPurchaseResult="addonPurchaseResult"
    :banner="banner"
    :bannerText="bannerText"
    :dismissBanner="dismissBanner"
    :trialWarning="trialWarning"
    :featureGate="featureGate"
    :featureMeteredUsageKey="featureMeteredUsageKey"
    :items="utilNavItems"
    :isPromoVisible="isPromoVisible"
    :navBarExpanded="navBarExpanded"
    :navigation="navigation"
    :plan="plan"
    :title="title"
    :upgradeLink="`${HC_PROPS.ACCOUNTS_APP_URL}/settings?modal=subscription`"
    :updateLink="`${HC_PROPS.ACCOUNTS_APP_URL}/settings?modal=paymentMethod`"
    :customNumberLink="`${HC_PROPS.ACCOUNTS_APP_URL}/settings?modal=customNumber`"
    :user="userInfo"
    :v2Only="v2Only"
    :verifyEmail="verifyEmailCta"
    :shouldLoadV2Contacts="shouldLoadV2Contacts"
    :shouldSeeV2ContactsToggle="shouldSeeV2ContactsToggle"
    :isUpdatingV2ContactsFlag="isUpdatingV2ContactsFlag"
    :hasAtLeastOneEmailAccount="hasAtLeastOneEmailAccount"
    :hasNylasReauthFF="hasNylasReauthFF"
    :shouldLoadV2Events="shouldLoadV2Events"
    :shouldLoadQualify="shouldLoadQualify"
    :isUpdatingQualifyFlag="isUpdatingQualifyFlag"
    :shouldSeeV2EventsToggle="shouldSeeV2EventsToggle"
    :isUpdatingV2EventsFlag="isUpdatingV2EventsFlag"
    :setShouldLoadV2Contacts="toggleShouldLoadFeatureFlag(contactsMS)"
    :setShouldLoadV2Events="toggleShouldLoadFeatureFlag(eventsMS)"
    :setShouldLoadQualify="toggleShouldLoadFeatureFlag(qualify)"
    :hasAccountManagementFF="hasAccountManagementFF"
    :hasBrokersSalesDemoFF="hasBrokersSalesDemoFF"
    :profilePicture="profilePicture"
    :createRelationshipLicense="createRelationshipLicense"
    :getUserULClients="getUserULClients"
    :showLWAConnectDialog="showLWAConnectDialog"
    :lwaConnectDismissalCount="lwaConnectDismissalCount"
    @accept:addon="acceptMeteredUsage"
    @click:resendVerifyEmail="resendVerifyEmail"
    @click:a2pRegistration="redirectToA2pRegistration"
    @logout="logout"
    @toggle:drawer="toggleDrawer"
    @toggle:gated="toggleFeatureGate(null)"
    @toggle:verifyEmail="toggleVerifyEmailCta"
    @updateProfilePicture="updateProfilePicture"
    @lwa:askLater="handleLwaAskLater"
    @lwa:connect="handleLwaConnect"
    class="layout-container"
  >
    <slot></slot>
    <template slot="search">
      <slot name="search"></slot>
    </template>
  </LdAppLayoutPresenter>
</template>

<script>
import Vue from 'vue';
import { createNamespacedHelpers } from 'vuex';
import { HC_PROPS } from 'lion-vue-src/_globals';
import LdFeatures from 'lion-vue-src/_globals/lib/LdFeatures';
import LdAppLayoutPresenter from './LdAppLayoutPresenter';
import {
  FEATURE_FLAG_CONTACTS_MS,
  FEATURE_FLAG_CONTACTS_MS_TOGGLE,
  FEATURE_FLAG_EVENTS_MS,
  FEATURE_FLAG_EVENTS_MS_TOGGLE,
  FEATURE_FLAG_QUALIFY,
} from '../../store/modules/ldAppLayout/store/constants';

const EMAIL_INTEGRATION = "email_integration_notification_dismissed";
const sunsetMLS = ['HngTojeR1aC0f2IuR', 'AzyWC9RwRzp7C23sH', 'AzZcjYS2r29CE1ngw', 'LIONDESK1327605PLANABC123'];
const nonSunsetMLS = ['16BcmCS3cUX8E1gG0','16BZALS3cTj2o1iHu','LIONDESK1327602PLANABC123','1ps2gs9RV5mK7htPP','2smoc9EuRF9ud1K1Hxi','AzZMr8Syri06W110k'];
const carMLS = ['16BcmCS3cUX8E1gG0','16BZALS3cTj2o1iHu', '1ps2gs9RV5mK7htPP','2smoc9EuRF9ud1K1Hxi','AzZMr8Syri06W110k'];

const {
  mapState: mapAppLayoutState,
  mapGetters: mapAppLayoutGetters,
  mapActions: mapAppLayoutActions,
  mapMutations: mapAppLayoutMutations
} = createNamespacedHelpers(
  'ldAppLayout',
);
const {
  mapState: mapSubscriptionState,
  mapActions: mapSubscriptionActions,
} = createNamespacedHelpers('subscription');

const {
  mapGetters: mapEmailManagementGetters,
  mapActions: mapEmailManagementActions,
} = createNamespacedHelpers('emailManagement');

export default {
  components: {
    LdAppLayoutPresenter,
  },
  props: {
    ignoreAuth: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'LionDesk 2.0 MVP',
    },
    activeApp: {
      type: String,
      default: '',
    },
  },
  data() {
    const navigation = [
      {
        id: 'home',
        icon: 'dashboard',
        title: 'Home',
        route: {
          name: 'dashboard',
          href: `${HC_PROPS.PLATFORM_APP_URL}/dashboard`,
          target: '_self',
        },
        app: 'crm',
      },
      {
        id: 'contacts',
        icon: 'contacts',
        title: 'Contacts',
        route: { name: 'contacts', href: `${HC_PROPS.PLATFORM_APP_URL}/contacts`, target: '_self' },
        app: 'crm',
      },
      {
        id: 'communication',
        icon: 'mail_outline',
        title: 'Communications',
        route: {
          name: 'communications',
          href: `${HC_PROPS.PLATFORM_APP_URL}/communications`,
          target: '_self',
        },
        app: 'crm',
      },
      {
        id: 'tasks',
        icon: 'event_note',
        title: 'Tasks',
        route: { name: 'tasks', href: `${HC_PROPS.PLATFORM_APP_URL}/tasks`, target: '_self' },
        app: 'crm',
      },
      {
        id: 'campaigns',
        icon: 'flight_takeoff',
        title: 'Campaigns',
        route: {
          name: 'campaigns',
          href: `${HC_PROPS.PLATFORM_APP_URL}/campaigns`,
          target: '_self',
        },
        app: 'crm',
      },
      {
        id: 'videos',
        icon: 'videocam',
        title: 'Videos',
        route: {
          name: 'videos',
          href: `${HC_PROPS.PLATFORM_APP_URL}/videos?sf=created_at&so=desc`,
          target: '_self',
        },
        app: 'crm',
      },
      {
        id: 'marketing',
        icon: '$vuetify.icons.bullhorn',
        title: 'Marketing',
        route: {
          name: 'marketing',
          href: `${HC_PROPS.PLATFORM_APP_URL}/marketing`,
          target: '_self',
        },
        app: 'crm',
      },
      {
        id: 'leadsplusreporting',
        icon: '$vuetify.icons.leads-plus',
        title: 'Leads+',
        route: {
          name: 'leadsplusreporting',
          href: `${HC_PROPS.PLATFORM_APP_URL}/leadsplusreporting`,
          target: '_self',
        },
        app: 'crm',
      },
      {
        id: 'documents',
        icon: 'folder',
        title: 'Documents',
        route: {
          name: 'documents',
          href: `${HC_PROPS.PLATFORM_APP_URL}/documents`,
          target: '_self',
        },
        app: 'crm',
      },
      {
        id: 'transactions',
        icon: 'credit_card',
        title: 'Transactions',
        route: { name: '/', href: HC_PROPS.TRANSACTIONS_APP_URL, target: '_self' },
        app: 'transactions',
        feature: LdFeatures.ApplicationTransactions,
      },
      // Uncomment this when VMI is released
      // {
      //   id: 'mls',
      //   icon: 'home',
      //   title: 'Visual Market Insights',
      //   route: { name: '/', href: HC_PROPS.MLS_APP_URL, target: '_self' },
      //   app: 'mls',
      //   feature: LdFeatures.ApplicationVmi,
      // },
      {
        id: 'reporting',
        icon: 'insert_chart_outlined',
        title: 'Reporting',
        route: {
          name: 'reporting',
          href: `${HC_PROPS.PLATFORM_APP_URL}/reporting`,
          target: '_self',
        },
        app: 'crm',
      },
      {
        id: 'help',
        icon: 'help',
        title: 'Online Help',
        route: { name: 'online-help', href: 'https://lonewolf.my.site.com/s/liondesk-resources', target: '_blank' },
        app: 'help',
      },
      {
        id: 'v1',
        icon: 'launch',
        title: 'LionDesk V1',
        route: { name: 'LionDeskV1', href: 'https://www.liondesk.com/admin/', target: '_blank' },
        app: 'v1',
      },
    ];
    return {
      LdFeatures,
      drawer: true,
      navigation,
      trialWarning: false,
      banner: false,
      HC_PROPS: HC_PROPS,
      contactsMS: FEATURE_FLAG_CONTACTS_MS,
      contactsMSToggle: FEATURE_FLAG_CONTACTS_MS_TOGGLE,
      eventsMS: FEATURE_FLAG_EVENTS_MS,
      eventsMSToggle: FEATURE_FLAG_EVENTS_MS_TOGGLE,
      qualify: FEATURE_FLAG_QUALIFY,
      creatingRelationshipsLicense: false,
      clientsPoller: null,
    };
  },
  watch: {
    userInfo: {
      async handler(value) {
        if (
          this.userInfo.id &&
          !this.$cookies.get(`UL_OFF_${this.userInfo.id}`) &&
          !this.nonSunsetMLSUser &&
          ((this.userInfo.saw_billing_migration === 'true' || this.userInfo.saw_billing_migration === true) || this.sunsetMLSUser)
        ) {
          await this.getUserULClients();
          if (Array.isArray(this.ulClientLicenses) && this.ulClientLicenses.includes('Relationships')) {
            window.location.href = 'https://crm.lwolf.com';
            return
          } else {
            window.location.href = 'https://www.lwolf.com/engage/relationships?utm_source=liondesk&utm_medium=redirect&utm_campaign=eol_upgrade';
            return
          }
        }
        if (this.$cookies.get('sunset_bypass') !== '1') {
          if (this.userInfo.id && this.isCARMLS) {
            window.location.href = 'https://lonewolf.my.site.com/s/article/LD-C-A-R-data-migration-from-LionDesk-to-Relationships'
            return
          }
        }
        this.setShouldSeeFFToggle(this.contactsMSToggle);
        this.setShouldLoadV2FFInitialState(this.contactsMS);

        this.setShouldSeeFFToggle(this.eventsMSToggle);
        this.setShouldLoadV2FFInitialState(this.eventsMS);

        this.setShouldLoadV2FFInitialState(this.qualify);

        this.banner = this.showBanner;
        this.trialWarning = this.showTrialWarning;

        window.lwcrm = { me: value };
        this.relationshipsRedirectFlow();
      },
      immediate: true,
    },
    ulClientLicenses: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (newValue && newValue.includes('Relationships')) {
          this.creatingRelationshipsLicense = false;
          if (this.clientsPoller) {
            clearInterval(this.clientsPoller);
            this.clientsPoller = null;
          }
          if (this.$route.query['relationships'] === 'true') {
            setTimeout(() => {
              window.location = `${this.HC_PROPS.UL_PLATFORM_URL}/product/launch?a=crm`
            }, 2000);
          }
        }
      },
    }
  },
  computed: {
    ...mapAppLayoutState([
      'navBarExpanded',
      'userInfo',
      'verifyEmailCta',
      'shouldLoadV2Contacts',
      'shouldSeeV2ContactsToggle',
      'isUpdatingV2ContactsFlag',
      'hasAtLeastOneEmailAccount',
      'shouldLoadV2Events',
      'shouldSeeV2EventsToggle',
      'isUpdatingV2EventsFlag',
      'isUpdatingQualifyFlag',
      'shouldLoadQualify',
      'onboardingFinished',
      'smsUsageInfo',
      'showChangeProfilePictureModal',
      'ulClientLicenses',
      'lwaDisconnectDismissalCountCalled',
      'lwaConnectDismissalCount',
      'lwaConnectLastDismissed',
      'relationshipsLicenseCreatedFound',
    ]),
    ...mapSubscriptionState([
      'addonPurchaseResult',
      'featureGate',
      'featureMeteredUsageAcceptedKey',
      'featureMeteredUsageKey',
      'features',
      'plan',
    ]),
    ...mapEmailManagementGetters([
      'hasAtLeastOneOutlookAccountToReconnect'
    ]),
    ...mapAppLayoutGetters([
      'hasAccountManagementFF',
      'hasBrokersSalesDemoFF',
      'profilePicture',
      'hasNylasReauthFF',
    ]),
    v2Only() {
      if (!this.userInfo) return true;
      const { account_version } = this.userInfo;
      return account_version === 2;
    },
    // reserved for future promotional banner use
    isPromoVisible() {
      return false;
    },
    utilNavItems() {
      return [];
    },
    showBanner() {
      const seen = JSON.parse(localStorage.getItem('paymentNagDismissed'));
      // If the user doesn't exist don't show banner. Prevents flash before user loads
      if (!this.userInfo) return false;

      // TODO: REMOVE THIS USER ID FILTER. The next two lines
      // if (this.MLS_DEMO_ALL.includes(this.userInfo.id)) return !seen;

      const { txt_status } = this.userInfo.subscriptionOverview;
      // If credit card (or trial user) is expired and the user has not yet dismissed, show CTA
      if (['ccexpired_pending', 'ccexpired', 'trial_expired'].includes(txt_status) && !seen)
        return true;
      // If user is not an MLS user don't show CTA
      if (txt_status !== 'mls') return false;
      // Show banner if user has not yet dismissed it
      return !seen;
    },
    bannerText() {
      return 'Add a payment method to send more emails and text messages than are included in your monthly plan. Please note that these are pay-per-use features after your monthly limit.';
    },
    showTrialWarning() {
      if (!this.userInfo) return false;
      if (this.userInfo.subscriptionOverview.txt_status == 'trial') {
        return true;
      }
      return false;
    },
    showLWAConnectDialog() {
      if (!this.userInfo) {
        return false;
      }

      if (this.$cookies.get(`UL_OFF_${this.userInfo.id}`) === 'true') {
        return false;
      }

      if (this.userInfo && this.userInfo.lwa_id) {
        return false;
      }

      if (!this.isEligibleUser(["pab"])) {
        return false;
      }

      if (!this.lwaDisconnectDismissalCountCalled) {
        return false;
      }

      const lastDismissedParsed = this.lwaConnectLastDismissed ? Date.parse(this.lwaConnectLastDismissed) : 0;
      if (this.lwaConnectDismissalCount === 0 || lastDismissedParsed <= Date.now() - 1000 * 60 * 60 * 24 * 1) {
        return true;
      }
      return false;
    },
    nonSunsetMLSUser() {
      return this.userInfo 
        && this.userInfo.subscriptionOverview 
        && nonSunsetMLS.includes(this.userInfo.subscriptionOverview.id_chargebee.String);
    },
    sunsetMLSUser() {
      return this.userInfo 
        && this.userInfo.subscriptionOverview
        && sunsetMLS.includes(this.userInfo.subscriptionOverview.id_chargebee.String);
    },
    isHawaiiMLS() {
      return this.userInfo 
        && this.userInfo.subscriptionOverview 
        && this.userInfo.subscriptionOverview.id_chargebee.String === 'LIONDESK1327602PLANABC123';
    },
    isCARMLS() {
      return this.userInfo 
        && this.userInfo.subscriptionOverview 
        && carMLS.includes(this.userInfo.subscriptionOverview.id_chargebee.String);
    }
  },
  methods: {
    ...mapAppLayoutActions([
      'setShowChangeProfilePictureModal',
      'setNavBarExpanded',
      'getUserInfo',
      'toggleVerifyEmailCta',
      'resendVerifyEmail',
      'setHasAtLeastOneEmailAccount',
      'getOnboardingInfo',
      'setShouldLoadFeatureFlag',
      'setShouldSeeFFToggle',
      'setShouldLoadV2FFInitialState',
      'createRelationshipLicense',
      'getUserULClients',
      'createLwaConnectTracking',
      'getLwaConnectDismissalCount',
    ]),
    ...mapAppLayoutMutations(['setLwaConnectDismissalCount']),
    ...mapSubscriptionActions(['toggleFeatureGate', 'acceptMeteredUsage']),
    ...mapEmailManagementActions(['loadEmailAccounts']),
    toggleDrawer() {
      this.$emit('toggle:drawer', !this.navBarExpanded);
      this.setNavBarExpanded(!this.navBarExpanded);
    },
    logout() {
      const ref = document.location.href;
      window.$cookies.set('LD_REDIRECT_URL', ref, null, null, HC_PROPS.MAIN_DOMAIN);
      window.$cookies.remove('LD_AUTH', null, HC_PROPS.MAIN_DOMAIN);
      window.$cookies.remove('feathers-jwt', null, HC_PROPS.MAIN_DOMAIN);
      window.$cookies.remove('RL6Token', null, HC_PROPS.MAIN_DOMAIN);
      window.lwcrm = undefined;
      window.location = `${HC_PROPS.ACCOUNTS_APP_URL}/signin`;
    },
    updateProfilePicture() {
      this.setShowChangeProfilePictureModal(true);
    },
    dismissBanner() {
      localStorage.setItem('paymentNagDismissed', true);
      this.banner = false;
    },
    redirectToA2pRegistration() {
      this.$router.push('/a2p-standardbrand-registration');
    },
    toggleShouldLoadFeatureFlag(flag) {
      return (shouldLoadFF) => {
        this.setShouldLoadFeatureFlag({ shouldLoadFF, flag })
      };
    },
    relationshipsRedirectFlow() {
      if (!this.userInfo || !this.userInfo.lwa_id) return;
      const isRelationshipsFlow = this.$route.query['relationships'] === 'true';
      this.creatingRelationshipsLicense = true;
      if (isRelationshipsFlow && this.userInfo.lwa_id) {
        this.clientsPoller = setInterval(() => {
          this.getUserULClients();
        }, 1000);
      }
    },
    handleLwaAskLater() {
      this.createLwaConnectTracking({ action: 'dismissed' });
      this.setLwaConnectDismissalCount({ count: this.lwaConnectDismissalCount + 1, last_dismissed: new Date().toISOString() });
    },
    handleLwaConnect() {
      const isEligibleForRelationships = this.isEligibleUser();

      let url = `${HC_PROPS.UL_PLATFORM_URL}/authenticate?r=${HC_PROPS.ACCOUNTS_APP_URL}/unified-login?&sc=signup&e=${this.userInfo.email}`;

      if (isEligibleForRelationships) {
        url += '&relationships=true';
      }

      window.location.href = url;
    },
    isEligibleUser(excludePlans = ["pab"]) {
      if (!this.userInfo || !this.userInfo.subscriptionOverview) {
        return false;
      }

      const subscriptionDetails = this.userInfo.subscriptionOverview

      const isPlanExcluded = excludePlans.includes(subscriptionDetails.txt_plan);

      let hasPromoteAgentBundleActiveItem = false;
      if (Array.isArray(subscriptionDetails.active_items)) {
        hasPromoteAgentBundleActiveItem = subscriptionDetails.active_items.some(item =>
          item.toLowerCase().includes("promote-agent-bundle")
        );
      }

      const isAllowedCohort = !isPlanExcluded && !hasPromoteAgentBundleActiveItem;

      const isAllowedUser = !this.nonSunsetMLSUser || this.isHawaiiMLS;
      
      return isAllowedCohort && isAllowedUser;
    },
    checkFeatureFlag(flag) {
      if (!this.userInfo) return false;
      const { feature_flags: featureFlags } = this.userInfo;
      return featureFlags.includes(flag);
    },
  },
  async created() {
    if (!this.ignoreAuth && this.$route.meta.getUserInfo === true) {
      await this.getUserInfo();
    }
    if (!this.lwaDisconnectDismissalCountCalled) {
      await this.getLwaConnectDismissalCount();
    }

    if (!localStorage.getItem(EMAIL_INTEGRATION)) {
      this.setHasAtLeastOneEmailAccount();
    }
    await this.loadEmailAccounts();
    const storedPrefs = localStorage.getItem('preferences');
    if (storedPrefs) {
      const storedPrefsJSON = JSON.parse(storedPrefs);
      if (storedPrefsJSON.navBarExpanded !== undefined) {
        this.$emit('toggle:drawer', storedPrefsJSON.navBarExpanded);
        this.setNavBarExpanded(storedPrefsJSON.navBarExpanded);
      }
    }

    if (this.userInfo && !this.userInfo.email_is_verified) {
      window.location = `${HC_PROPS.PLATFORM_APP_URL}/resend-verification-email`
    }

    await this.getOnboardingInfo();
    if (!this.onboardingFinished) {
      window.location = `${HC_PROPS.PLATFORM_APP_URL}/onboarding`
    }
  },
  beforeMount() {
    this.banner = this.showBanner;
  },
};
</script>
